import './home.less'

import {m, Picture, wrap} from '@codeurs/front'
import format from 'date-fns/format'
import {useList} from 'hooks/uselist'
import requestService from 'mithril/request/request'
import {Component, FunctionComponent} from 'react'
import {CardData, ImageType, LinkType, PageData, PaginationResult} from 'types'
import {formatDate} from 'util/formatDate'
import {useLanguage} from 'util/i18n'
import {PageLink} from 'util/pagelink'
import {getLocale} from 'util/params'
import {style} from 'util/style'
import {Layout} from 'views/layout'
import {ArticleCard, ArticleGrid} from 'views/partials/articlegrid'
import {LoadMore} from 'views/partials/loadmore'
import {Newsletter} from 'views/partials/newsletter'
import {Theme} from 'views/theme'
import {CardCategory, CardDate} from '../layout/card'
import {HeaderPortal} from '../partials/header'
import {ImageLoader} from '../partials/imageloader'

const request = requestService(window, Promise).request

const styles = style('home', ['newsletter', 'header', 'links'])

const HomeLinkImage = styles
	.links('image')
	.toElement<ImageType & {title?: string}>(image => {
		if (!image || !image.src) return null
		return (
			<div class={styles.links('image-container')}>
				<Picture {...image} alt={image.alt || image.title} />
			</div>
		)
	})

type HomeLink = {
	image: ImageType
	link: LinkType
	text: string
}

const BlueHero: FunctionComponent<CardData> = ({
	category,
	title,
	url,
	date,
	image
}) => {
	return (
		<Theme.Container class={styles.header('blue')}>
			<div class={styles.header('blue-content')}>
				<CardCategory color={category && category.color}>
					<div class={styles.header('category')}>{category.title}</div>
				</CardCategory>
				<CardDate class={styles('date').mod('light')}>
					{date && formatDate(date)}
				</CardDate>
				<Theme.H1 class={styles.header('title')}>
					<PageLink to={url}>{title}</PageLink>
				</Theme.H1>
			</div>
			<div class={styles.header('blue-image-placeholder')} />
		</Theme.Container>
	)
}

@wrap(Layout)
export class Home extends Component<{
	data: PageData & {
		articles: PaginationResult<CardData>
		featured: CardData
		featured_articles: Array<CardData & {half_page: boolean}>
		links: Array<HomeLink>
		featured_blue: boolean
	}
}> {
	state = {isComplete: false}

	getListStartAmount() {
		const {featured_articles} = this.props.data
		let amount = 12
		featured_articles.forEach(fa => {
			amount--
			if (fa.half_page) amount--
		})
		return amount
	}

	render() {
		const {
			url,
			language,
			articles,
			newsletter,
			featured,
			links,
			featured_blue,
			tagslist,
			tagspath,
			featured_articles
		} = this.props.data
		const list = useList<CardData>({
			url,
			data: {json: 'articles'},
			hydrate: articles,
			amounts: {
				start: this.getListStartAmount(),
				page: 12
			}
		})
		return (
			<div class={styles}>
				<div class={styles('container')}>
					<Theme.Container>
						{featured && (
							<HeaderPortal>
								{featured_blue ? (
									<BlueHero {...featured} />
								) : (
									<Theme.Container>
										<div class={styles.header}>
											<CardCategory color={featured.category.color}>
												<div class={styles.header('category')}>
													{featured.category.title}
												</div>
											</CardCategory>
											<CardDate class={styles('date').mod('light')}>
												{featured.date &&
													format(
														new Date(featured.date.replace(/-/g, '/')),
														'd MMMM yyyy',
														{
															locale: getLocale(useLanguage())
														}
													)}
											</CardDate>
											<Theme.H1 class={styles.header('title')}>
												<PageLink to={featured.url}>{featured.title}</PageLink>
											</Theme.H1>
										</div>
									</Theme.Container>
								)}
							</HeaderPortal>
						)}
						{featured && featured_blue && (
							<div class={styles.header('blue-image-container')}>
								<div class={styles.header('blue-image-container-box')}>
									<ImageLoader
										image={{...featured.image, fit: 'cover'}}
										class={styles.header('blue-image').toString()}
										header={true}
									/>
								</div>
							</div>
						)}
					</Theme.Container>
					<ArticleGrid hasMore={list.hasMore} blue={featured && featured_blue}>
						{featured_articles.map((item, i) => (
							<ArticleCard
								item={{key: i, ...item}}
								tagspath={tagspath}
								tagslist={tagslist}
								half={item.half_page}
							/>
						))}
						{list.items.map((item, i) => (
							<ArticleCard
								item={{key: i, ...item}}
								tagspath={tagspath}
								tagslist={tagslist}
							/>
						))}
					</ArticleGrid>
					{list.hasMore && (
						<div className={styles('button')}>
							<LoadMore
								loading={list.loading}
								loadMore={() => list.loadMore()}
							/>
						</div>
					)}
					<Newsletter {...newsletter} language={language} mod="home">
						{!!links.length && (
							<div class={styles.links}>
								{links.map(item => (
									<div class={styles.links('gap')}>
										<div class={styles.links('item')}>
											<Theme.Link
												to={item.link ? item.link.url : '/'}
												target="_blank"
											>
												<HomeLinkImage {...item.image} />
												<div class={styles.links('textcontainer')}>
													<Theme.H2 class={styles.links('text')}>
														{item.text}
													</Theme.H2>
												</div>
											</Theme.Link>
										</div>
									</div>
								))}
							</div>
						)}
					</Newsletter>
				</div>
			</div>
		)
	}
}
